import { ApiError } from '../api/client.ts';
import { NeedsSignIn, useSession } from '../auth/session.tsx';

/**
 * Whether a failure is about the session rather than the thing asked for.
 *
 * A 401 always is. A 403 depends on which route said it. From `/me` or the
 * client list it is the authenticator refusing the account itself — no staff
 * row linked to this sign-in, or a deactivated one — because those routes have
 * no per-client check. From a single client's routes it is usually row-level
 * security saying "outside your caseload", which is a true answer about that
 * client, so those callers pass `forbiddenIsSession: false` and ask `/me`
 * which of the two it was.
 */
export function isSessionFailure(
  error: unknown,
  { forbiddenIsSession = true }: { forbiddenIsSession?: boolean } = {},
): boolean {
  if (error instanceof NeedsSignIn) return true;
  if (!(error instanceof ApiError)) return false;
  return error.status === 401 || (forbiddenIsSession && error.status === 403);
}

/**
 * What a screen shows when the request behind it failed and there is no good
 * answer to show instead.
 *
 * **Never an empty state.** "No clients" is a statement about the data, and a
 * failed request has said nothing about the data. On Azure, with every request
 * returning 401, the client list rendered as an empty list: an authorisation
 * failure presented as a calm and false fact, on the screen people will be
 * looking at if anything goes wrong in the room. An empty state belongs to a
 * successful response with zero rows and to nothing else.
 *
 * So a failure says which kind it is. A session failure says the session is not
 * valid and offers to sign in again; signing out first clears the cached
 * account, so the same refused sign-in is not silently reused. Anything else
 * says the thing could not be loaded, and whether the server answered at all.
 */
export function LoadFailure({
  what,
  error,
  onRetry,
  retrying = false,
  forbiddenIsSession = true,
  verb = 'could not be loaded',
}: {
  /** Sentence subject: "The client list", "This client". */
  what: string;
  error: Error;
  onRetry: () => void;
  retrying?: boolean;
  forbiddenIsSession?: boolean;
  /** What did not happen: "could not be loaded", or "could not be run" for a search. */
  verb?: string;
}) {
  const { signOut } = useSession();
  const said = error instanceof ApiError ? error : null;

  if (isSessionFailure(error, { forbiddenIsSession })) {
    return (
      <div role="alert" className="p-6">
        <h2 className="text-lg font-bold text-neutral-900">Your session is not valid</h2>
        <p className="mt-2 text-neutral-700">
          {said ? said.message : 'The server did not accept your sign-in.'}
        </p>
        {said?.hint && <p className="mt-1 text-neutral-600">{said.hint}</p>}
        <p className="mt-1 text-neutral-500">Nothing was loaded.</p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-5 min-h-[48px] rounded-lg bg-neutral-900 px-5 font-semibold text-white
                     active:bg-neutral-700"
        >
          Sign in again
        </button>
      </div>
    );
  }

  return (
    <div role="alert" className="p-6">
      <h2 className="text-lg font-bold text-neutral-900">{what} {verb}</h2>
      <p className="mt-2 text-neutral-700">
        {said
          ? `The server reported an error: ${said.message}`
          : 'The server could not be reached.'}
      </p>
      {said?.hint && <p className="mt-1 text-neutral-600">{said.hint}</p>}
      {retrying ? (
        <p className="mt-5 text-neutral-500">Trying again…</p>
      ) : (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 min-h-[48px] rounded-lg bg-neutral-900 px-5 font-semibold text-white
                     active:bg-neutral-700"
        >
          Try again
        </button>
      )}
    </div>
  );
}
